import { HttpClient } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, finalize, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { unwrap } from '../api/api-response';
import { ApiResponse, LoginResult } from '../api/api.types';
import { TokenStore } from './token.store';

@Service()
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokens = inject(TokenStore);
  private readonly router = inject(Router);
  private readonly base = `${environment.apiUrl}admin/auth`;

  private readonly _username = signal<string | null>(null);
  readonly username = this._username.asReadonly();
  readonly isLoggedIn = computed(() => this._username() !== null || !!this.tokens.access);

  private refreshing$: Observable<boolean> | null = null;

  login(username: string, password: string): Observable<void> {
    return this.http.post<ApiResponse<LoginResult>>(`${this.base}/login`, { username, password }).pipe(
      unwrap(),
      tap((r) => {
        this.tokens.set(r.accessToken, r.refreshToken);
        this._username.set(username);
      }),
      map(() => undefined),
    );
  }

  me(): Observable<string> {
    return this.http.get<ApiResponse<{ username: string }>>(`${this.base}/me`).pipe(
      unwrap(),
      map((r) => r.username),
      tap((u) => this._username.set(u)),
    );
  }

  /** One shared in-flight refresh so parallel 401s don't each call it. Emits true on success. */
  refresh(): Observable<boolean> {
    const refreshToken = this.tokens.refresh;
    if (!refreshToken) return of(false);
    this.refreshing$ ??= this.http
      .post<ApiResponse<{ accessToken: string }>>(
        `${this.base}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${refreshToken}` } },
      )
      .pipe(
        map((res) => {
          const access = res.data?.accessToken;
          if (!access) return false;
          this.tokens.set(access);
          return true;
        }),
        shareReplay(1),
        finalize(() => (this.refreshing$ = null)),
      );
    return this.refreshing$;
  }

  logout(redirect = true): void {
    this.tokens.clear();
    this._username.set(null);
    if (redirect) void this.router.navigate(['/admin/login']);
  }
}
